import React, { useEffect, useRef, useState } from "react";
import { Link, useOutletContext } from "react-router";
import { useApi } from "/src/helpers/Hooks/useApi";
import formatMoney from "/src/helpers/formatMoney";
import Button from "/src/helpers/components/button.jsx";
import SecureTradingCheckout from "/src/components/SecureTradingCheckout.jsx";
import PickupMap, { walkLabel } from "/src/helpers/components/pickupMap.jsx";
import { track, ecommerce, basketItems } from "/src/helpers/analytics";
import { rateBreakdown, partyOf } from "/src/helpers/excursionRates";
import {
    ArrowLeftIcon, TrashIcon, CalendarIcon, PinIcon, CarIcon, CartIcon, CameraIcon,
    CheckIcon, SteeringIcon, InfoCircleIcon, extraIconFor,
} from "/src/helpers/components/upsellIcons.jsx";

const gbp = (v) => `£${formatMoney(v)}`;
const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "";
const fmtCarDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "";
// "Sat 7 Aug 2026 · 14:00" — the hire window carries resort-local times (buffered from the flights).
const fmtCarDateTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return `${fmtCarDate(iso)} · ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const fmtShortDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return `${d.getDate()} ${d.toLocaleDateString("en-GB", { month: "short", year: "numeric" })}`;
};
const TERMS_URL = "https://www.olympicholidays.com/help/booking-terms-and-conditions/";
// Same links the booking journey's terms confirmation uses (ProjectTitan paxDetailsPage).
const PRIVACY_URL = "https://www.olympicholidays.com/about-us/privacy-policy/";
const HOLIDAY_INFO_URL = "https://www.olympicholidays.com/help/holiday-information/";

const fmtDob = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB") : null);
// An <option> can't carry markup, so the lead passenger is called out in the text (they're also
// sorted to the top of the driver list).
const driverOptionLabel = (g) => {
    const name = [g.title, g.firstName, g.lastName].filter(Boolean).join(" ");
    const dob = fmtDob(g.dateOfBirth);
    return `${name}${dob ? ` · ${dob}` : ""}${g.isLead ? " · Lead passenger" : ""}`;
};

// Per-line traveller selector. Excursions keep the toggleable guest pills; the car line's
// "Who's driving?" is a dropdown of the booking's adults (lead first, then title, name and date
// of birth).
const GuestPicker = ({ line, guests, driver, onToggleGuest, onSetDriver }) => {
    const list = driver
        ? guests.filter((g) => g.type === "Adult").sort((a, b) => (b.isLead ? 1 : 0) - (a.isLead ? 1 : 0))
        : guests;
    if (driver) {
        return (
            <div>
                <div className="text-[11px] font-bold text-content_3 uppercase tracking-[0.05em] mb-2">
                    Who's driving?
                </div>
                <div className="flex items-center gap-2.5">
                    <span className="w-[30px] h-[30px] rounded-full shrink-0 flex items-center justify-center bg-emp text-white">
                        <SteeringIcon size={15} />
                    </span>
                    <select value={line.driverId || ""} onChange={(e) => onSetDriver(Number(e.target.value))}
                            className="px-3 py-2 rounded-lg border border-content_5 bg-white text-sm text-primary cursor-pointer max-w-full min-w-0">
                        <option value="" disabled>Select the main driver</option>
                        {list.map((g) => (
                            <option key={g.bookingPassengerId} value={g.bookingPassengerId}>
                                {driverOptionLabel(g)}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        );
    }
    return (
        <div>
            <div className="text-[11px] font-bold text-content_3 uppercase tracking-[0.05em] mb-2">
                Who's going?
            </div>
            <div className="flex flex-wrap gap-2">
                {list.map((g) => {
                    const on = driver
                        ? line.driverId === g.bookingPassengerId
                        : line.guestIds.includes(g.bookingPassengerId);
                    return (
                        <button key={g.bookingPassengerId}
                                onClick={() => (driver ? onSetDriver(g.bookingPassengerId) : onToggleGuest(g.bookingPassengerId))}
                                title={driver ? "Set as driver" : on ? "Tap to remove" : "Tap to add"}
                                className={`inline-flex items-center gap-2 py-[5px] pl-[5px] pr-3 rounded-full border-[1.5px] cursor-pointer
                                    ${on ? "border-emp bg-emp_soft" : "border-content_5 bg-white opacity-60"}`}>
                            <span className={`w-[26px] h-[26px] rounded-full shrink-0 flex items-center justify-center text-white
                                ${on ? "bg-emp" : "bg-white border-[1.5px] border-content_4"}`}>
                                {on && (driver ? <SteeringIcon /> : <CheckIcon />)}
                            </span>
                            <span className="text-[13px] font-semibold text-primary">{g.firstName}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const RemoveButton = ({ onClick }) => (
    <button onClick={onClick} aria-label="Remove"
            className="shrink-0 flex items-center gap-1.5 text-[13px] font-semibold text-content_3 hover:text-error cursor-pointer">
        <TrashIcon /> Remove
    </button>
);

const MetaChip = ({ icon, children }) => (
    <span className="inline-flex items-center gap-[7px]">
        <span className="text-emp_dark">{icon}</span>{children}
    </span>
);

const CheckoutPage = () => {
    const { model, refreshModel } = useOutletContext();
    const api = useApi();

    const [basket, setBasket] = useState(null);
    const [guests, setGuests] = useState([]);
    const [carExtrasCatalogue, setCarExtrasCatalogue] = useState(null);
    const [quote, setQuote] = useState(null);
    const [quoting, setQuoting] = useState(false);
    const [quoteError, setQuoteError] = useState("");
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [carTermsAccepted, setCarTermsAccepted] = useState(false);
    // Read at checkout time (not from whichever render created the callback): the payment form's
    // approval callback can be created before the boxes are ticked.
    const termsRef = useRef({ termsAccepted: false, carTermsAccepted: false });
    termsRef.current = { termsAccepted, carTermsAccepted };
    // Whether the booking has any excursions to sell — null until known. Only looked up for a
    // basket with no excursions in it (there's nothing to "browse" on e.g. a 1-night Corfu stay).
    const [excursionsAvailable, setExcursionsAvailable] = useState(null);
    const [paying, setPaying] = useState(false);
    // The payment box renders below the summary; bring it into view when it appears.
    const paymentRef = useRef(null);
    useEffect(() => {
        if (paying) setTimeout(() => paymentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    }, [paying]);
    const [done, setDone] = useState(null);
    // Checkout completes from the payment box at the foot of a long page; the confirmation that
    // replaces it is short, so without this the customer is left looking at the footer.
    useEffect(() => {
        if (done) window.scrollTo(0, 0);
    }, [done]);
    const [error, setError] = useState("");
    const quoteSeq = useRef(0);

    useEffect(() => {
        (async () => {
            const [res, guestList] = await Promise.all([api.basketGet(), api.excursionGuests()]);
            setBasket(res.data);
            setGuests(guestList || []);
            if (basketItems(res.data).length) {
                // Funnel: the basket IS the checkout, so both fire on arrival.
                ecommerce.viewCart(res.data);
                ecommerce.beginCheckout(res.data);
            }
            if ((res.data?.excursions?.length || 0) === 0) {
                const rows = await api.excursionSearch(true); // silent background fetch, as the home page does
                setExcursionsAvailable((rows?.length || 0) > 0);
            } else setExcursionsAvailable(true);
        })();
    }, []);

    // The car's optional extras stay editable at checkout (prototype §checkout) — the catalogue
    // comes from the search token the car was picked with.
    const carToken = basket?.car?.searchToken;
    useEffect(() => {
        if (!carToken) { setCarExtrasCatalogue(null); return; }
        (async () => {
            const list = await api.carHireExtras(carToken);
            setCarExtrasCatalogue((list || []).filter((e) => !e.includedInPrice && !e.mandatory));
        })();
    }, [carToken]);

    const lineCount = (basket?.excursions?.length || 0) + (basket?.car ? 1 : 0);
    const excCount = basket?.excursions?.length || 0;
    const countLabel = [
        excCount && `${excCount} ${excCount === 1 ? "excursion" : "excursions"}`,
        basket?.car && "1 car hire",
    ].filter(Boolean).join(" · ");

    const carDays = (() => {
        const c = basket?.car;
        if (!c?.pickupDate || !c?.dropoffDate) return null;
        // Calendar days between the two dates: a 14:00 pick-up and 11:00 drop-off a week apart is
        // still 7 days, so the buffered times never shave a day off the per-day price.
        const day = (iso) => { const d = new Date(iso); return new Date(d.getFullYear(), d.getMonth(), d.getDate()); };
        return Math.max(1, Math.round((day(c.dropoffDate) - day(c.pickupDate)) / 86400000));
    })();

    // Prices are re-confirmed automatically (single terms-gated pay button, as the prototype):
    // any basket mutation invalidates the quote and this effect re-prebooks it.
    const requestQuote = async () => {
        const seq = ++quoteSeq.current;
        setQuoting(true);
        setQuoteError("");
        const res = await api.basketQuote(true);
        if (seq !== quoteSeq.current) return; // superseded by a later basket change
        setQuoting(false);
        if (res.ok) setQuote(res.data);
        else setQuoteError(res.data?.detail || "We couldn't confirm your prices — please try again.");
    };

    useEffect(() => {
        if (!basket || lineCount === 0 || done || paying) return;
        if (quote || quoting || quoteError) return;
        const t = setTimeout(requestQuote, 300);
        return () => clearTimeout(t);
    }, [basket, quote, quoting, quoteError, done, paying]);

    const invalidateQuote = () => { quoteSeq.current++; setQuote(null); setQuoting(false); setQuoteError(""); };

    const removeLine = async (lineId) => {
        const res = await api.basketRemoveLine(lineId);
        if (res.ok) {
            const line = (basket?.excursions || []).find((l) => l.lineId === lineId);
            const removed = line ? basketItems({ excursions: [line] })[0]
                : basket?.car?.lineId === lineId ? basketItems({ car: basket.car })[0] : null;
            if (removed) ecommerce.removeFromCart(removed);
            setBasket(res.data); invalidateQuote();
        }
    };

    const setLineGuests = async (lineId, guestIds, driverId = null) => {
        setError("");
        if (guestIds.length === 0) return; // keep at least one, like the prototype
        const res = await api.basketSetLineGuests(lineId, { guestIds, driverId });
        if (res.ok) { setBasket(res.data); invalidateQuote(); }
        else setError(res.data?.detail || "We couldn't update the guests.");
    };

    const toggleExcursionGuest = (line) => (id) => {
        const next = line.guestIds.includes(id)
            ? line.guestIds.filter((g) => g !== id)
            : [...line.guestIds, id];
        if (next.length === 0) return;
        setLineGuests(line.lineId, next);
    };

    const setDriver = (id) => setLineGuests(basket.car.lineId, basket.car.guestIds, id);

    // Pick-up point is price-neutral: the server keeps the quote, so no invalidation here.
    const setLinePickup = async (lineId, pickupPointId) => {
        setError("");
        const res = await api.basketSetLinePickup(lineId, pickupPointId);
        if (res.ok) setBasket(res.data);
        else setError(res.data?.detail || "We couldn't change the pick-up point.");
    };

    const selectedExtraTokens = (basket?.car?.extras || []).map((e) => e.extraToken);
    const toggleCarExtra = async (extra) => {
        const on = selectedExtraTokens.includes(extra.token);
        const next = on
            ? (basket.car.extras || []).filter((e) => e.extraToken !== extra.token)
            : [...(basket.car.extras || []), { extraToken: extra.token, quantity: 1 }];
        const res = await api.basketSetCarExtras(next);
        if (res.ok) { setBasket(res.data); invalidateQuote(); }
        else setError(res.data?.detail || "We couldn't update the extras — please try again.");
    };
    const payAtDeskExtras = (carExtrasCatalogue || [])
        .filter((e) => selectedExtraTokens.includes(e.token) && !e.prePaid);
    const payAtDeskTotal = payAtDeskExtras.reduce((s, e) => s + e.price, 0);

    const completeCheckout = async (paymentToken = null) => {
        setError("");
        const res = await api.basketCheckout({ ...termsRef.current, paymentToken });
        if (res.ok) {
            ecommerce.purchase(res.data?.ancillaryReference || res.data?.bookingReference || `${model?.bookingReference}-${Date.now()}`,
                quote?.total ?? basket?.estimatedTotal, basket,
                { paid_by_card: !!paymentToken, lines: basketItems(basket).length });
            await refreshModel();
            setDone(res.data);
        } else {
            track("checkout_failed", { reason: res.data?.title || res.status, paid_by_card: !!paymentToken });
            setPaying(false);
            invalidateQuote();
            setError(res.data?.detail || "Checkout failed — your prices may have changed, please try again.");
        }
    };

    const paymentCustomer = (() => {
        const cd = model?.customerDetails || {};
        return {
            firstName: cd.firstName, lastName: cd.lastName, email: cd.email,
            addressLine1: cd.address1, addressLine2: cd.address2, townCity: cd.townCity,
            postcode: cd.postcode, countryCode: cd.countryCode,
        };
    })();

    const backLink = (
        <Link to="/" className="inline-flex items-center gap-1.5 text-emp_dark font-semibold text-sm py-2 mb-4">
            <ArrowLeftIcon /> Back to My Booking
        </Link>
    );

    if (done) {
        return (
            <div>
                {backLink}
                <div className="bg-white rounded-card shadow-card p-6">
                    <h1 className="text-2xl font-poppins font-bold text-primary mb-2">All booked! 🎉</h1>
                    <p className="text-content_2 mb-4">
                        Your additional extras have been added to booking <span className="font-semibold text-primary">{done.bookingReference}</span>
                        {done.bookingReference !== model?.bookingReference ? " and have been paid in full" : ""}.
                    </p>
                    <Link to="/"><Button variant="primary">Back to My Booking</Button></Link>
                </div>
            </div>
        );
    }

    if (basket === null) {
        return <div className="bg-white rounded-card shadow-card p-6 text-content_2">Loading your basket…</div>;
    }

    if (lineCount === 0) {
        return (
            <div>
                {backLink}
                <div className="bg-white rounded-card shadow-card px-[30px] py-[60px] text-center">
                    <span className="inline-block text-[#cfe1ef] mb-3.5"><CartIcon size={48} strokeWidth={1.6} /></span>
                    <h2 className="text-[22px] font-poppins font-bold text-primary mb-1.5">Your basket is empty</h2>
                    <p className="text-content_2 mb-[18px]">Add an excursion or two to get started.</p>
                    <Link to="/?excursions=1"><Button variant="primary">Browse excursions</Button></Link>
                </div>
            </div>
        );
    }

    const car = basket.car;

    const lineCard = (line, isCar) => (
        <section key={line.lineId}
                 className="bg-white rounded-card shadow-card overflow-hidden flex flex-col md:flex-row md:items-stretch">
            <div className="w-full aspect-video md:aspect-auto md:w-[200px] md:shrink-0 bg-[#0e2533]">
                {line.imageUrl
                    ? <img src={line.imageUrl} alt="" className="w-full h-full object-cover block" />
                    : <div className="w-full h-full min-h-[120px] flex items-center justify-center text-white/40">{isCar ? <CarIcon size={40} /> : <CameraIcon size={40} />}</div>}
            </div>
            <div className="px-[22px] py-[18px] flex-1 min-w-0 flex flex-col">
                <div className="flex justify-between gap-3">
                    <h3 className="text-lg font-poppins font-bold text-primary leading-tight">
                        {isCar ? (line.vehicleDescription || "Car hire") : line.name}
                    </h3>
                    <RemoveButton onClick={() => removeLine(line.lineId)} />
                </div>
                <div className="flex flex-wrap gap-x-[18px] gap-y-1.5 mt-2.5 text-[13px] text-content_2">
                    {isCar ? (
                        <>
                            {line.supplier && <MetaChip icon={<CarIcon />}>{line.supplier}</MetaChip>}
                            {line.pickupDate && line.dropoffDate && (
                                <MetaChip icon={<CalendarIcon />}>{fmtCarDateTime(line.pickupDate)} → {fmtCarDateTime(line.dropoffDate)}</MetaChip>
                            )}
                            {line.depotName && <MetaChip icon={<PinIcon />}>{line.depotName}</MetaChip>}
                        </>
                    ) : (
                        <>
                            <MetaChip icon={<CalendarIcon />}>{fmtDate(line.useDate)}{line.startTime ? ` · ${line.startTime}` : ""}</MetaChip>
                            {(line.pickupPoints || []).length === 0 && (
                                <MetaChip icon={<PinIcon />}>{line.baseDestination}</MetaChip>
                            )}
                        </>
                    )}
                </div>
                {!isCar && (line.pickupPoints || []).length > 0 && (() => {
                    const points = line.pickupPoints;
                    const selected = points.find((p) => p.pickupPointId === line.pickupPointId) || points[0];
                    // Map only when the pick-up isn't the customer's own hotel — then it's always shown.
                    const mappable = !line.pickupLocked
                        && (points.some((p) => p.latitude != null && p.longitude != null) || line.hotelLatitude != null);
                    return (
                        <>
                        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-[13px]">
                            <span className="inline-flex items-center gap-[7px] text-content_3 font-semibold uppercase tracking-[0.05em] text-[11px] shrink-0">
                                <span className="text-emp_dark"><PinIcon /></span> Pick-up point
                            </span>
                            {line.pickupLocked || points.length <= 1 ? (
                                <>
                                    <span className="text-primary font-medium">{selected.location}{selected.time ? `, ${selected.time}` : ""}</span>
                                    {line.pickupLocked && (
                                        <span className="inline-flex items-center gap-1 bg-[#e7f6ef] text-[#157a54] px-2 py-0.5 rounded-full text-[11px] font-bold shrink-0">
                                            <CheckIcon size={10} /> Your hotel
                                        </span>
                                    )}
                                </>
                            ) : (
                                <select value={selected?.pickupPointId ?? ""}
                                        onChange={(e) => setLinePickup(line.lineId, Number(e.target.value))}
                                        className="px-3 py-1.5 rounded-lg border border-content_5 bg-white text-[13px] text-primary cursor-pointer max-w-full min-w-0">
                                    {points.map((p) => (
                                        <option key={p.pickupPointId} value={p.pickupPointId}>
                                            {p.location}{p.time ? ` · ${p.time}` : ""}{walkLabel(p) ? ` · ${walkLabel(p)}` : ""}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                        {mappable && (
                            <div className="mt-2.5">
                                <PickupMap points={points} selectedId={selected?.pickupPointId} height={200}
                                           onSelect={(id) => setLinePickup(line.lineId, id)}
                                           hotel={line.hotelLatitude != null ? { name: line.hotelName, latitude: line.hotelLatitude, longitude: line.hotelLongitude } : null} />
                                {selected?.mapUrl && (
                                    <a href={selected.mapUrl} target="_blank" rel="noopener noreferrer"
                                       className="inline-block mt-1.5 text-[12px] text-content_3 hover:underline">Open in Google Maps ↗</a>
                                )}
                            </div>
                        )}
                        </>
                    );
                })()}
                <div className="mt-3.5 mb-3.5">
                    <GuestPicker line={line} guests={guests} driver={isCar}
                                 onToggleGuest={toggleExcursionGuest(line)} onSetDriver={setDriver} />
                </div>
                <div className="mt-auto pt-3 border-t border-content_5 flex justify-between items-baseline">
                    <span className="text-[13px] text-content_3">
                        {isCar
                            ? (carDays ? `${carDays} ${carDays === 1 ? "day" : "days"}${line.estimatedTotal ? ` · ${gbp(line.estimatedTotal / carDays)}/day` : ""}` : "Car hire")
                            : (() => {
                                // Rates for the guests actually on this line (child/infant only when selected).
                                const rates = rateBreakdown(line, partyOf(guests.filter((g) => line.guestIds.includes(g.bookingPassengerId))));
                                const count = `${line.guestIds.length} ${line.guestIds.length === 1 ? "guest" : "guests"}`;
                                if (!rates) return count;
                                const others = rates.others.map((o) => `${o.label.toLowerCase()} ${o.free ? "free" : gbp(o.price)}`);
                                return [count, `${gbp(rates.headline)}${rates.headlineSuffix}`, ...others].join(" · ");
                            })()}
                    </span>
                    <span className="text-xl font-bold text-primary">{gbp(line.estimatedTotal)}</span>
                </div>
            </div>
        </section>
    );

    // Rental conditions arrive ##-delimited from the supplier; the leading key-facts block is
    // additionally pipe-delimited ("FUEL - Full to Full |EXCESS - …"). The prebook text can also
    // come with the pipes turned into blank lines and the colons inside the facts turned into
    // single line breaks ("at 20\r\n29 (GMT)") — split on all of those, restore the colons.
    const conditionItems = (car?.rentalConditions || "")
        .split(/##\s*|\r?\n[ \t]*\r?\n/)
        .flatMap((s) => (s.includes("|") ? s.split("|") : [s]))
        .map((s) => s.trim().replace(/[ \t]*\r?\n[ \t]*/g, ":").replace(/^Terms and Conditions:?\s*$/i, ""))
        // Suppliers embed HTML line breaks in the longer sections (deposit / excess tables,
        // fuel policy) — honour them as real breaks and drop any other stray markup.
        .map((s) => s.replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]+>/g, "").replace(/[ \t]+\n/g, "\n").replace(/\n{2,}/g, "\n").trim())
        .filter(Boolean);

    const carTermsOk = !car || carTermsAccepted;
    const termsOk = termsAccepted && carTermsOk;

    // The two acceptances, rendered in the payment box (card payments) or the summary (on account).
    const termsCheckboxes = (className) => (
        <div className={className}>
            {car && (
                <label className="flex items-start gap-2.5 text-[13px] text-primary leading-relaxed cursor-pointer">
                    <input type="checkbox" checked={carTermsAccepted} onChange={(e) => setCarTermsAccepted(e.target.checked)}
                           className="w-[18px] h-[18px] mt-px shrink-0 accent-emp cursor-pointer" />
                    <span className="font-semibold">
                        I have read and accept the{" "}
                        <a href="#car-hire-information" className="text-emp_dark underline"
                           onClick={(e) => { e.preventDefault(); document.getElementById("car-hire-information")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}>
                            important car hire information
                        </a>.
                    </span>
                </label>
            )}
            <label className={`${car ? "mt-3" : ""} flex items-start gap-2.5 text-[13px] text-content_2 leading-relaxed cursor-pointer`}>
                <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)}
                       className="w-[18px] h-[18px] mt-px shrink-0 accent-emp cursor-pointer" />
                <span>
                    I confirm that I have read and understood the{" "}
                    <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-emp_dark underline">Terms &amp; Conditions</a>,{" "}
                    <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-emp_dark underline">Privacy Policy</a>{" "}
                    and the{" "}
                    <a href={HOLIDAY_INFO_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-emp_dark underline">Additional Holiday Information</a>{" "}
                    for Olympic Holidays.
                </span>
            </label>
        </div>
    );

    return (
        <div>
            {backLink}
            <div className="text-xs font-semibold text-content_3 uppercase tracking-[0.06em] mb-3.5">
                Checkout · {countLabel}
            </div>
            {error && <div className="bg-error_2 text-error text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

            <div className="grid grid-cols-1 md:grid-cols-[1.6fr_1fr] gap-[22px] items-start">
                {/* ── basket lines ─────────────────────────────────────────────────────── */}
                <div className="flex flex-col gap-[18px]">
                    {(basket.excursions || []).map((line) => lineCard(line, false))}
                    {car && lineCard(car, true)}

                    {/* Contextual navigation (prototype): car-only baskets say "Browse excursions" —
                        but only when the booking actually has excursions on offer. */}
                    {excursionsAvailable && (
                        <Link to="/?excursions=1"
                              className="self-start inline-flex items-center gap-2 border-[1.5px] border-[#cfe1ef] rounded-btn px-[18px] py-[11px] text-emp_dark font-semibold text-sm hover:bg-emp_soft">
                            <ArrowLeftIcon />
                            {excCount === 0 ? "Browse excursions" : "Add more excursions"}
                        </Link>
                    )}

                    {car && carExtrasCatalogue?.length > 0 && (
                        <section className="bg-white rounded-card shadow-card px-8 py-6">
                            <h3 className="text-lg font-poppins font-bold text-primary mb-1">Optional car hire extras</h3>
                            <p className="text-[13.5px] text-content_2 mb-4">
                                Reserve now, <strong className="text-primary">pay at the rental desk</strong>{" "}
                                <span className="text-content_3">(unless marked prepaid)</span>.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {carExtrasCatalogue.map((ex) => {
                                    const on = selectedExtraTokens.includes(ex.token);
                                    return (
                                        <button key={ex.token} onClick={() => toggleCarExtra(ex)}
                                                className={`text-left flex items-center gap-3 px-3.5 py-[13px] rounded-[11px] cursor-pointer
                                                    ${on ? "border-[1.5px] border-emp bg-emp_soft" : "border border-content_5 bg-white"}`}>
                                            <span className={`w-[38px] h-[38px] rounded-[9px] shrink-0 flex items-center justify-center
                                                ${on ? "bg-emp text-white" : "bg-[#E8EEF4] text-emp_dark"}`}>
                                                {extraIconFor(ex.description)}
                                            </span>
                                            <span className="flex-1 min-w-0">
                                                <span className="block font-semibold text-sm text-primary">{ex.description}</span>
                                                <span className="block text-xs text-content_3">{ex.prePaid ? "Prepaid — added to your total" : "Pay at the rental desk"}</span>
                                            </span>
                                            <span className="text-right shrink-0">
                                                <span className="block font-bold text-sm text-primary">{gbp(ex.price)}</span>
                                            </span>
                                            <span className={`w-[22px] h-[22px] rounded-md shrink-0 flex items-center justify-center text-white
                                                ${on ? "bg-emp" : "border-[1.5px] border-[#cfe1ef] bg-white"}`}>
                                                {on && <CheckIcon size={13} />}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                            {payAtDeskTotal > 0 && (
                                <div className="mt-3.5 px-4 py-3 bg-[#E8EEF4] rounded-[10px] flex justify-between items-center">
                                    <span className="text-[13.5px] text-content_2">
                                        Extras payable at pickup{carDays ? ` (${carDays} ${carDays === 1 ? "day" : "days"})` : ""}
                                    </span>
                                    <span className="font-bold text-primary">{gbp(payAtDeskTotal)}</span>
                                </div>
                            )}
                        </section>
                    )}
                </div>

                {/* ── order summary, sticky (prototype aside) ──────────────────────────── */}
                <aside className="bg-white rounded-card shadow-card px-8 py-6 md:sticky md:top-4 self-start">
                    <h3 className="text-lg font-poppins font-bold text-primary mb-3.5">Order summary</h3>
                    <div className="text-sm text-primary">
                        {(basket.excursions || []).map((l) => (
                            <div key={l.lineId} className="flex justify-between items-start gap-3 py-1.5">
                                <span className="min-w-0">
                                    {l.name}
                                    <span className="block text-[13px] text-content_2">{fmtShortDate(l.useDate)}</span>
                                </span>
                                <span className="shrink-0 font-semibold">{gbp(l.estimatedTotal)}</span>
                            </div>
                        ))}
                        {car && (
                            <div className="flex justify-between items-start gap-3 py-1.5">
                                <span className="min-w-0">
                                    {car.vehicleDescription || "Car hire"}
                                    {carDays ? <span className="block text-[13px] text-content_2">{carDays} day{carDays === 1 ? "" : "s"}</span> : null}
                                </span>
                                <span className="shrink-0 font-semibold">{gbp(car.estimatedTotal)}</span>
                            </div>
                        )}
                    </div>
                    <div className="border-t border-content_5 my-3.5" />
                    <div className="flex justify-between items-baseline">
                        <span className="font-semibold text-primary">Total</span>
                        <span className="text-[26px] font-bold text-primary">{gbp(quote ? quote.total : basket.estimatedTotal)}</span>
                    </div>
                    {payAtDeskTotal > 0 && (
                        <div className="mt-3 px-3 py-2.5 bg-[#fff4e3] rounded-[9px] text-[13px] text-[#b66a0a]">
                            <div className="font-semibold mb-1">Pay at rental desk</div>
                            {/* Itemised so it's clear what the desk will charge for (not part of today's total). */}
                            {payAtDeskExtras.map((e) => (
                                <div key={e.token} className="flex justify-between items-start gap-3 py-0.5">
                                    <span className="min-w-0">{e.description}{carDays ? ` · ${carDays} day${carDays === 1 ? "" : "s"}` : ""}</span>
                                    <span className="shrink-0">{gbp(e.price)}</span>
                                </div>
                            ))}
                            {payAtDeskExtras.length > 1 && (
                                <div className="flex justify-between items-center gap-2 pt-1 mt-1 border-t border-[#f1d9b0] font-bold">
                                    <span>Total at desk</span>
                                    <span>{gbp(payAtDeskTotal)}</span>
                                </div>
                            )}
                        </div>
                    )}
                    {quoteError && (
                        <p className="text-xs text-error mt-3">{quoteError}</p>
                    )}

                    {/* Card payments: the terms are ticked in the payment box below (Trello #469 —
                        no scrolling past them to reach the card fields). On-account confirmations
                        have no payment box, so they tick them here. */}
                    {quote && !quote.requiresPayment && termsCheckboxes("mt-4")}

                    {quoteError ? (
                        <button onClick={requestQuote}
                                className="mt-3.5 w-full px-[18px] py-[14px] rounded-xl bg-emp hover:bg-emp_dark text-white font-bold text-[15px] cursor-pointer">
                            Try again
                        </button>
                    ) : !paying && (() => {
                        const ready = !!quote && (quote.requiresPayment || termsOk);
                        return (
                            <>
                                <button disabled={!ready}
                                        onClick={() => { if (quote.requiresPayment) { ecommerce.addPaymentInfo(basket, quote.total); setPaying(true); } else completeCheckout(); }}
                                        className={`mt-3.5 w-full px-[18px] py-[14px] rounded-xl font-bold text-[15px]
                                            ${ready ? "bg-emp hover:bg-emp_dark text-white cursor-pointer" : "bg-[#aebfcf] text-white cursor-not-allowed"}`}>
                                    {!quote ? "Confirming prices…"
                                        : quote.requiresPayment ? `Continue to payment · ${gbp(quote.total)}`
                                        : "Confirm and add to booking"}
                                </button>
                                {quote && !quote.requiresPayment && termsAccepted && !carTermsOk && (
                                    <p className="text-xs text-content_3 mt-2">Please accept the important car hire information to continue.</p>
                                )}
                            </>
                        );
                    })()}
                </aside>
            </div>

            {/* ── important car hire information: full width, read before paying — the payment
                   box (with the acceptances and Confirm & Pay) follows it. ─────────────────── */}
            {car && (
                <section id="car-hire-information" className="rounded-card bg-[#f0f7fd] border border-[#cfe5f7] px-8 py-6 mt-[22px] scroll-mt-4">
                    <div className="flex items-center gap-2.5 mb-3 text-emp_dark">
                        <InfoCircleIcon />
                        <h3 className="text-lg font-poppins font-bold text-primary">Important car hire information</h3>
                    </div>
                    {conditionItems.length > 0 ? (
                        // One full-width column: the supplier writes these as a sequence that reads in
                        // order, which a two-column grid scrambled (row-by-row across the columns).
                        <ul className="flex flex-col gap-[9px]">
                            {conditionItems.map((t, i) => (
                                <li key={i} className="flex gap-[9px] text-[13.5px] text-content_2 leading-relaxed">
                                    <span className="shrink-0 mt-0.5 text-emp_dark"><CheckIcon size={15} strokeWidth={2.5} /></span>
                                    <span className="whitespace-pre-line">{t}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-[13.5px] text-content_2 leading-relaxed">
                            The rental conditions (driver documents, deposit, fuel policy and excess) are set by the car hire
                            provider and apply at pick-up.
                        </p>
                    )}
                </section>
            )}

            {/* ── payment (shared TrustPayments hosted fields) — the terms are ticked here, right
                   above the card fields, after the car hire information (Trello #469). ───────── */}
            {paying && quote?.requiresPayment && (
                <div ref={paymentRef} className="bg-white rounded-card shadow-card px-8 py-6 mt-[22px] scroll-mt-4">
                    <h2 className="text-lg font-poppins font-bold text-primary mb-3">Payment</h2>
                    <div className="rounded-[10px] bg-bg_2 px-4 py-3 mb-5">
                        {termsCheckboxes("")}
                    </div>
                    <SecureTradingCheckout
                        amount={quote.total}
                        customerDetails={paymentCustomer}
                        payButtonText={`Pay ${gbp(quote.total)}`}
                        onApproved={(jwt) => completeCheckout(jwt)}
                        disabled={!termsOk}
                        disabledHint={!termsAccepted ? "Please confirm you've read the terms above to pay."
                            : "Please accept the important car hire information to pay."}
                    />
                </div>
            )}
        </div>
    );
};

export default CheckoutPage;
